import Loading from "@/src/components/loading";
import { openToast } from "@/src/utils/toasts";
import {
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { FiEdit2, FiMoreVertical } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import {
  addPromiseToWindow,
  AiDescription,
  removePromiseFromWindow,
} from "../app/aiTasks/aiDescription";
import { uid } from "../utils/utils";

export type ButtonColor =
  | "simple"
  | "gray"
  | "yellow"
  | "red"
  | "green"
  | "blue"
  | "orange"
  | "purple"
  | "white"
  | "pink";

export const buttonColors = {
  simple: {
    id: "simple",
    name: "Simple",
    icon: null,
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  white: {
    id: "white",
    name: "Blanc",
    icon: null,
    color: "rgba(255, 255, 255,1)",
    secondaryColor: "rgba(255, 255, 255,0.1)",
  },
  gray: {
    id: "gray",
    name: "Gris",
    icon: null,
    color: "rgba(0, 0, 0,0.8)",
    secondaryColor: "rgba(0, 0, 0,0.1)",
  },
  red: {
    id: "red",
    name: "Rouge",
    icon: null,
    color: "rgba(255, 0, 0,1)",
    secondaryColor: "rgba(255, 0, 0,0.1)",
  },
  green: {
    id: "green",
    name: "Vert",
    icon: null,
    color: "rgba(2, 194, 75, 1)",
    secondaryColor: "rgba(2, 194, 75,0.12)",
  },
  blue: {
    id: "blue",
    name: "Bleu",
    icon: null,
    color: "rgba(0, 85, 255,1)",
    secondaryColor: "rgba(0, 85, 255,0.1)",
  },
  yellow: {
    id: "yellow",
    name: "Jaune",
    icon: null,
    color: "rgba(255, 193, 7,1)",
    secondaryColor: "rgba(255, 193, 7,0.1)",
  },
  orange: {
    id: "orange",
    name: "Orange",
    icon: null,
    color: "rgba(255, 124, 2,1)",
    secondaryColor: "rgba(255, 124, 2,0.1)",
  },
  purple: {
    id: "purple",
    name: "Violet",
    icon: null,
    color: "rgba(144, 0, 255,1)",
    secondaryColor: "rgba(144, 0, 255,0.1)",
  },
  pink: {
    id: "pink",
    name: "Rose",
    icon: null,
    color: "rgba(255, 77, 115,1)",
    secondaryColor: "rgba(255, 77, 115,0.1)",
  },
};

//forward ref

export const Button = forwardRef(
  (
    {
      type = "primary",
      id,
      description,
      isDisabled,
      disabledMessage,
      additionalStyle,
      icon,
      color = "blue",
      onClick,
      children,
    }: {
      type?: "primary" | "secondary" | "tertiary" | "quaternary";
      id?: string;
      description?: string;
      color?: ButtonColor;
      icon?: any;
      isDisabled?: boolean;
      disabledMessage?: string;
      additionalStyle?: React.CSSProperties;
      onClick: MouseEventHandler;
      children?: any;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    let colorInfo = color ? buttonColors[color] : null;

    let [isLoading, setIsLoading] = useState(false);

    // const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (id && typeof window !== "undefined") {
        (window as any).items ||= {};
        (window as any).items[id] = {
          ref: ref as any,
        };
      }
      return () => {
        if (id && typeof window !== "undefined") {
          delete (window as any).items[id];
        }
      };
    }, [id]);

    let child = children;
    if (icon)
      child = (
        <div className="cauto-1 gap5 acenter " style={{ width: "100%" }}>
          {icon} <span className="mright10">{children}</span>
        </div>
      );

    if (isLoading) {
      child = <Loading size={15} girth={2}></Loading>;
    }

    function getBackgroundColor() {
      if (type == "tertiary") return colorInfo?.secondaryColor;
      if (type == "secondary") return undefined;
      if (type == "quaternary") return undefined;
      return colorInfo?.color;
    }

    function getBorderColor() {
      if (type == "secondary") return colorInfo?.color;
      return undefined;
    }

    function getColor() {
      if (type == "secondary" || type == "tertiary") return colorInfo?.color;
      if (type == "quaternary") return colorInfo?.color;
      return undefined;
    }

    return (
      <AiDescription
        item={{
          type: "button",
          title: child,
          description,
          ref: ref as any,
          isClickable: true,
        }}
      >
        <div
          ref={ref as any}
          className={`btn relative ${isLoading ? "loading" : ""} ${
            isDisabled ? "disabled" : ""
          } ${type} ${color}-btn`}
          style={{
            background: getBackgroundColor(),
            borderColor: getBorderColor(),
            color: getColor(),
            ...(additionalStyle || {}),
          }}
          onClick={async (e) => {
            if (isDisabled) {
              openToast("error", disabledMessage || "Action non autorisée");
              return;
            }
            let promiseId = uid();
            try {
              e.stopPropagation();
              if (isLoading == true) return;
              // addPromiseToWindow(promiseId);
              setIsLoading(true);
              if (onClick) await onClick(e);
              setIsLoading(false);
              // removePromiseFromWindow(promiseId);
            } catch (err) {
              console.log("the errooor", err);
              setIsLoading(false);
              // removePromiseFromWindow(promiseId);
              openToast("error", (err as any)?.message || err);
              throw e;
            }
          }}
        >
          {child}
        </div>
      </AiDescription>
    );
  }
);
Button.displayName = "Button";

export function IconButton({
  isMain,
  height,
  width,
  isSecondary,
  onClick,
  children,
}: {
  isMain?: boolean;
  height?: number;
  width?: number;
  isSecondary?: boolean;
  onClick: (e: any) => void;
  children?: any;
}) {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div
      className={`iconButton ${isSecondary ? "secondary" : ""} ${
        isMain ? "main" : ""
      } ${isLoading ? "disabled" : ""}`}
      style={{ height, width }}
      onClick={async (e) => {
        e.stopPropagation();
        if (isLoading) return;
        let promiseId = uid();
        try {
          // addPromiseToWindow(promiseId);
          setIsLoading(true);
          await onClick(e);
          setIsLoading(false);
          // removePromiseFromWindow(promiseId);
        } catch (e: any) {
          setIsLoading(false);
          // removePromiseFromWindow(promiseId);
          openToast("error", e?.message || e?.toString());
        }
      }}
    >
      {children}
    </div>
  );
}

export function SimpleIconButton({
  onClick,
  children,
}: {
  onClick: (e: any) => void;
  children?: any;
}) {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div
      className={`${isLoading ? "disabled" : ""}`}
      onClick={async (e) => {
        e.stopPropagation();
        if (isLoading) return;
        let promiseId = uid();
        try {
          // addPromiseToWindow(promiseId);
          setIsLoading(true);
          await onClick(e);
          setIsLoading(false);
          // removePromiseFromWindow(promiseId);
        } catch (e: any) {
          setIsLoading(false);
          // removePromiseFromWindow(promiseId);
          openToast("error", e?.message || e?.toString());
        }
      }}
    >
      {children}
    </div>
  );
}

export function CornerIconButton({
  type,
  position = "primary",
  iconSize,
  onClick,
  children,
}: {
  type: "edit" | "delete" | "remove" | "more" | "add";
  iconSize?: number;
  position?: "primary" | "secondary";
  onClick: (e: any) => void;
  children?: any;
}) {
  const [isLoading, setIsLoading] = useState(false);

  function getIcon() {
    if (children) return children;
    if (type === "edit") return <FiEdit2 size={iconSize || 16} />;
    if (type === "more") return <FiMoreVertical size={iconSize || 16} />;
    if (type === "add") return <IoMdAdd size={iconSize || 16} />;
    if (type === "delete") return <FaRegTrashAlt size={iconSize || 16} />;
    if (type === "remove")
      return <IoClose size={iconSize || 15} color="white"></IoClose>;
    return <FiEdit2 size={iconSize || 18} />;
  }

  async function onButtonClick(e: any) {
    let promiseId = uid();
    try {
      e.stopPropagation();
      if (isLoading) return;
      // addPromiseToWindow(promiseId);
      setIsLoading(true);
      await onClick(e);
      setIsLoading(false);
      // removePromiseFromWindow(promiseId);
    } catch (e: any) {
      setIsLoading(false);
      // removePromiseFromWindow(promiseId);
      openToast("error", e?.message || e?.toString());
      throw e;
    }
  }

  return (
    <div
      className={`cornerButton ${position} ${isLoading ? "loading" : ""}`}
      onClick={onButtonClick}
    >
      {isLoading ? <Loading size={20} girth={3}></Loading> : getIcon()}
    </div>
  );
}
